// RendererContext.js
import {
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "preact/hooks";
import { RendererApi, initExperienceRenderer } from "../renderer";
import { createContext, ReactNode } from "preact/compat";
import { Object3D } from "three";
import { artworkData } from "../renderer/artworks";
import { UIContentState } from "@/renderer/image-targets";

export enum RendererState {
  NONE,
  PLACED,
  LOADED,
  VIEWING,
}
type RendererContextType = {
  renderer: RendererApi | null;
  rendererState: RendererState;
  trackingStatus: "LIMITED" | "NORMAL";
  setTrackingStatus: (status: "LIMITED" | "NORMAL") => void;
  loadArtwork: (id: number) => Promise<void> | void;
  initExperience: () => Promise<void> | void;
  clearCurrentArtwork: () => void;
  uiState: UIContentState;
};

export const RendererContext = createContext<RendererContextType>({
  renderer: null,
  rendererState: RendererState.NONE,
  trackingStatus: "LIMITED",
  setTrackingStatus: (status: "LIMITED" | "NORMAL") => {},
  initExperience: () => {},
  loadArtwork: (id: number) => {},
  clearCurrentArtwork: () => {},
  uiState: UIContentState.NONE,
});

export const useRenderer = () => {
  return useContext(RendererContext);
};

export type RendererProviderProps = {
  children: ReactNode;
};

export const RendererProvider = ({ children }: RendererProviderProps) => {
  const [renderer, setRenderer] = useState<RendererApi | null>(null);
  const [rendererState, setRendererState] = useState(RendererState.NONE);
  const [trackingStatus, setTrackingStatus] = useState<"LIMITED" | "NORMAL">(
    "LIMITED"
  );
  const [uiState, setUIState] = useState<UIContentState>(UIContentState.NONE);

  const currentModelRef = useRef<Object3D | null>(null); // Track the current model

  const initExperience = useCallback(async () => {
    const canvasEl = document.getElementById("xr-canvas") as HTMLCanvasElement;
    if (!canvasEl) throw new Error("No Canvas element.");
    try {
      const renderer = await initExperienceRenderer(canvasEl, {
        watermarkImageUrl: "",
      });
      setRenderer(renderer);
    } catch (error) {
      console.error("initExperience error", error);
    }
  }, []);

  const clearCurrentArtwork = useCallback(() => {
    if (currentModelRef.current) {
      renderer?.getScene().remove(currentModelRef.current);
      currentModelRef.current = null;
    }
  }, [renderer]);

  const loadArtwork = useCallback(
    async (id: number) => {
      if (!renderer)
        throw new Error(`loadArtwork(${id}) but no experience loaded.`);
      await renderer.loadContent(id);
    },
    [renderer]
  );

  useEffect(() => {
    if (!renderer) return;

    const handleLoaded = () => {
      setRendererState(RendererState.LOADED);
    };

    const handleTrackingStatus = (status: "LIMITED" | "NORMAL") => {
      setTrackingStatus(status);
    };

    const handleUIState = (state: UIContentState) => {
      setUIState(state);
    };

    renderer.on("content-loaded", handleLoaded);
    renderer.on("tracking-status", handleTrackingStatus);
    renderer.on("set-ui-state", handleUIState);
    return () => {
      renderer.off("content-loaded", handleLoaded);
      renderer.off("tracking-status", handleTrackingStatus);
      renderer.off("set-ui-state", handleUIState);
    };
  }, [renderer, clearCurrentArtwork]);

  return (
    <RendererContext.Provider
      value={{
        renderer,
        rendererState,
        trackingStatus,
        setTrackingStatus,
        initExperience,
        loadArtwork,
        clearCurrentArtwork,
        uiState,
      }}
    >
      {children}
    </RendererContext.Provider>
  );
};
